import React from "react";
import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import {useNotify, useRefresh} from "react-admin";
import {client} from "../../dataProvider";

const eur = (v: number | string) =>
    new Intl.NumberFormat("it-IT", {style: "currency", currency: "EUR"}).format(
        typeof v === "string" ? Number(v) : v ?? 0
    );

export const ApprovedField = ({id}: { id: number }) => {
    const [rejectOpen, setRejectOpen] = React.useState(false);
    const [reason, setReason] = React.useState("");
    const [loading, setLoading] = React.useState<"approve" | "reject" | null>(null);
    const notify = useNotify();
    const refresh = useRefresh();

    const updateInvoice = async (approved: boolean, comment?: string) => {
        await client(`invoices/${id}`, {method: "PUT", data: {approved}});
        if (!approved && comment) {
            await client("approved_invoices/create_comment", {
                method: "POST",
                data: {id, comment}
            });
        }
    };

    const handleApprove = async () => {
        try {
            setLoading("approve");
            await updateInvoice(true);
            notify("Fattura approvata", "info");
            refresh();
        } catch (e) {
            notify("Errore durante l'approvazione", "warning");
        } finally {
            setLoading(null);
        }
    };

    const handleRejectConfirm = async () => {
        if (!reason.trim()) return;
        try {
            setLoading("reject");
            await updateInvoice(false, reason.trim());
            notify("Fattura rifiutata", "info");
            setRejectOpen(false);
            setReason("");
            refresh();
        } catch (e) {
            notify("Errore durante il rifiuto", "warning");
        } finally {
            setLoading(null);
        }
    };

    return (
        <>
            <Box display="flex" gridGap={8}>
                <Tooltip title="Approva fattura">
          <span>
            <Button
                variant="contained"
                color="primary"
                onClick={handleApprove}
                startIcon={<CheckCircleIcon/>}
                disabled={!!loading}
                style={{backgroundColor: "#2e7d32", color: "white"}}
            >
              {loading === "approve" ? <CircularProgress size={18}/> : "Approva"}
            </Button>
          </span>
                </Tooltip>

                <Tooltip title="Rifiuta fattura">
          <span>
            <Button
                variant="contained"
                color="secondary"
                onClick={() => setRejectOpen(true)}
                startIcon={<CancelIcon/>}
                disabled={!!loading}
                style={{backgroundColor: "#c62828"}}
            >
              Rifiuta
            </Button>
          </span>
                </Tooltip>
            </Box>

            <Dialog
                open={rejectOpen}
                onClose={() => (loading ? null : setRejectOpen(false))}
                aria-labelledby="reject-dialog-title"
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle id="reject-dialog-title">Motivo del rifiuto</DialogTitle>
                <DialogContent dividers>
                    <TextField
                        label="Inserisci il motivo"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        autoFocus
                        helperText="Il motivo sarà salvato come commento sulla fattura."
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setRejectOpen(false)}
                        startIcon={<CloseIcon/>}
                        disabled={!!loading}
                    >
                        Annulla
                    </Button>
                    <Button
                        onClick={handleRejectConfirm}
                        color="secondary"
                        variant="contained"
                        startIcon={
                            loading === "reject" ? <CircularProgress size={18}/> : <CancelIcon/>
                        }
                        disabled={!reason.trim() || !!loading}
                    >
                        Conferma rifiuto
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

/* ===========================
   SHOW COMMENT (tutti i commenti)
   =========================== */
export const ShowComment = ({comments = [] as any[]}) => {
    const [open, setOpen] = React.useState(false);

    return (
        <>
            <Tooltip title="Visualizza commenti">
                <IconButton
                    aria-label="visualizza commenti"
                    onClick={() => setOpen(true)}
                    size="small"
                >
                    <VisibilityIcon/>
                </IconButton>
            </Tooltip>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                aria-labelledby="comments-dialog-title"
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle id="comments-dialog-title">
                    Commenti
                    <IconButton
                        aria-label="chiudi"
                        onClick={() => setOpen(false)}
                        style={{position: "absolute", right: 8, top: 8}}
                    >
                        <CloseIcon/>
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    {(!comments || comments.length === 0) && (
                        <Typography variant="body2" color="textSecondary">
                            Nessun commento presente.
                        </Typography>
                    )}

                    {comments && comments.length > 0 && (
                        <List>
                            {comments.map((c: any, idx: number) => (
                                <React.Fragment key={`comment_${c.id ?? idx}`}>
                                    <ListItem alignItems="flex-start">
                                        <ListItemAvatar>
                                            <Avatar>{c.user?.username?.[0]?.toUpperCase() ?? "U"}</Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Box display="flex" alignItems="center" gridGap={8}>
                                                    <Typography variant="subtitle2">
                                                        {c.user?.username ?? "Utente"}
                                                    </Typography>
                                                    <Chip
                                                        size="small"
                                                        label={new Date(c.date).toLocaleString("it-IT")}
                                                        variant="default"
                                                    />
                                                </Box>
                                            }
                                            secondary={<Typography variant="body2">{c.comment}</Typography>}
                                        />
                                    </ListItem>
                                    {idx < comments.length - 1 && <Divider component="li"/>}
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};


export const ShowInvoiceLIneField = ({invoice_line = [] as any[]}) => {
    const [open, setOpen] = React.useState(false);

    const totals = React.useMemo(() => {
        const base = invoice_line.reduce(
            (acc, r: any) => {
                const qty = Number(r.quantity) || 0;
                const price = Number(r.price) || 0;
                const amount = Number(r.amount) || qty * price;
                const vat = Number(r.vat) || 0;
                return {
                    qty: acc.qty + qty,
                    taxable: acc.taxable + qty * price,
                    vat: acc.vat + vat,
                    amount: acc.amount + amount
                };
            },
            {qty: 0, taxable: 0, vat: 0, amount: 0}
        );
        return base;
    }, [invoice_line]);

    return (
        <>
            <Tooltip title="Dettagli righe fattura">
                <IconButton aria-label="dettagli fattura" onClick={() => setOpen(true)} size="small">
                    <VisibilityIcon/>
                </IconButton>
            </Tooltip>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                aria-labelledby="lines-dialog-title"
                fullWidth
                maxWidth="md"
            >
                <DialogTitle id="lines-dialog-title">
                    Dettagli fattura
                    <IconButton
                        aria-label="chiudi"
                        onClick={() => setOpen(false)}
                        style={{position: "absolute", right: 8, top: 8}}
                    >
                        <CloseIcon/>
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    <TableContainer component={Paper} style={{maxHeight: 420}}>
                        <Table stickyHeader aria-label="righe fattura">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Descrizione</TableCell>
                                    <TableCell align="right">Quantità</TableCell>
                                    <TableCell align="right">Prezzo</TableCell>
                                    <TableCell align="right">IVA</TableCell>
                                    <TableCell align="right">Importo</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {invoice_line.map((row: any) => (
                                    <TableRow hover key={`invoice_line_${row.id}`}>
                                        <TableCell component="th" scope="row">
                                            <Typography variant="body2">
                                                {row.replaced_description ?? row.description ?? "-"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">{row.quantity}</TableCell>
                                        <TableCell align="right">{eur(row.price)}</TableCell>
                                        <TableCell align="right">{eur(row.vat)}</TableCell>
                                        <TableCell align="right">{eur(row.amount)}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell/>
                                    <TableCell align="right">
                                        <strong>{totals.qty}</strong>
                                    </TableCell>
                                    <TableCell align="right">
                                        <strong>{eur(totals.taxable)}</strong>
                                    </TableCell>
                                    <TableCell align="right">
                                        <strong>{eur(totals.vat)}</strong>
                                    </TableCell>
                                    <TableCell align="right">
                                        <strong>{eur(totals.amount)}</strong>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
            </Dialog>
        </>
    );
};
